/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, breakpoints } from '@coursera/cds-core';

import type { BannerAdRowProps } from 'bundles/promo-units/components/banner-ad';
import { BANNER_AD_COUNTS_BY_PLACEMENT } from 'bundles/promo-units/components/banner-ad';
import BannerAd from 'bundles/promo-units/components/banner-ad/BannerAd';
import BannerAdPlaceholder from 'bundles/promo-units/components/banner-ad/BannerAdPlaceholder';
import { pixelMarginBetweenAds } from 'bundles/promo-units/components/banner-ad/constants';

// Why "Soapbox?"
// The CSS name "banner-ad" triggers ad blockers.
// Why two containers, inner and outer?
// TrackedDiv injects an extra div which above itself
// when withVisibilityTracking is set!
// And that div gets no styling / cannot be styled!

const styles = {
  soapboxRowStyles: css`
    margin: var(--cds-spacing-200) auto;
    max-width: ${564 * 2 + pixelMarginBetweenAds}px;
    ${breakpoints.down('xs')} {
      max-width: 100%;
    }
  `,
  soapBox: css`
    ${breakpoints.up('md')} {
      max-width: unset;
      flex-grow: 1;
    }
    ${breakpoints.down('xs')} {
      width: 100%;
    }
  `,
};

/**
 * A responsive row component that displays one or two banner advertisements.
 *
 * @description
 * This component renders a grid of banner advertisements with responsive behavior.
 * It supports both single and double banner layouts, with loading placeholders
 * when data is being fetched. The component uses "Soapbox" naming to avoid
 * ad-blocker interference.
 *
 * @note
 * This is AI generated documentation. Please use caution.
 */
const BannerAdRow = (props: BannerAdRowProps) => {
  const { adData, className, heightInPixels, onRefUpdated, placement, loading } = props;
  const hasAdData = !!adData?.length;

  if (!hasAdData && !loading) {
    return null;
  }

  const shouldShowLoadingPlaceholder = loading && !hasAdData;

  if (shouldShowLoadingPlaceholder) {
    const count = placement && BANNER_AD_COUNTS_BY_PLACEMENT[placement];
    const renderMultiplePlaceholders = count && count > 1;
    return (
      <div className={className} css={styles.soapboxRowStyles} ref={onRefUpdated}>
        <Grid container spacing={pixelMarginBetweenAds}>
          <Grid item md={renderMultiplePlaceholders ? 6 : 12} xs={12} css={styles.soapBox}>
            <BannerAdPlaceholder className="item" heightInPixels={heightInPixels} />
          </Grid>
          {renderMultiplePlaceholders && (
            <Grid item md={6} xs={12} css={styles.soapBox}>
              <BannerAdPlaceholder className="item" heightInPixels={heightInPixels} />
            </Grid>
          )}
        </Grid>
      </div>
    );
  }

  const renderMultiple = adData.length > 1;

  return (
    <div css={styles.soapboxRowStyles} className={className} ref={onRefUpdated}>
      <Grid container spacing={pixelMarginBetweenAds}>
        <Grid item md={renderMultiple ? 6 : 12} xs={12} css={styles.soapBox}>
          {/*
           // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess */}
          <BannerAd {...adData[0]} className="item" heightInPixels={heightInPixels} />
        </Grid>
        {renderMultiple && (
          <Grid item md={6} xs={12} css={styles.soapBox}>
            {/*
             // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess */}
            <BannerAd {...adData[1]} className="item" heightInPixels={heightInPixels} />
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default BannerAdRow;
